import { theme } from '../constants';
import { formatFieldValue, isBlank, sectionState } from '../function';
import { EditIcon } from './Icons';

const STATE_PILL = {
  on: { label: 'Active', color: '#047857', background: 'rgba(16,185,129,0.12)' },
  off: { label: 'Inactive', color: '#b45309', background: 'rgba(245,158,11,0.14)' },
  unset: { label: 'Not set', color: theme.textMuted, background: '#f3f4f6' },
};

/**
 * One sync column for one device: a status pill, a chip per configured key and
 * the last-updated stamp. Values are what the device reports it is running, so
 * a change it has not applied yet shows as a pending badge rather than by
 * quietly displaying the new numbers. The whole cell is the edit affordance.
 */
function SyncCell({ section, values, pending, onEdit, saving }) {
  const state = sectionState(section, values);
  const pill = STATE_PILL[state];

  const stampField = section.fields.find((field) => field.type === 'timestamp');
  const stamp = stampField && !isBlank(values[stampField.key]) ? formatFieldValue(stampField, values[stampField.key]) : null;

  // `active` is already carried by the pill and the stamp has its own line, so
  // neither is repeated as a chip.
  const chips =
    state === 'unset'
      ? []
      : section.fields
          .filter((field) => field.key !== 'active' && field.type !== 'timestamp' && !isBlank(values[field.key]))
          .map((field) => ({ key: field.key, label: field.label, text: formatFieldValue(field, values[field.key]) }));

  return (
    <button
      type="button"
      className="dsSyncCell"
      onClick={onEdit}
      disabled={saving}
      title={`Edit ${section.label}`}
      style={{
        width: '100%',
        textAlign: 'left',
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        background: theme.surface,
        padding: '9px 11px',
        cursor: saving ? 'progress' : 'pointer',
        fontFamily: theme.font,
        opacity: saving ? 0.6 : 1,
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 800,
            letterSpacing: 0.2,
            padding: '2.5px 8px',
            borderRadius: 6,
            color: pill.color,
            background: pill.background,
            flexShrink: 0,
          }}
        >
          {saving ? 'Saving…' : pill.label}
        </span>
        {pending && !saving && (
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: 0.2,
              padding: '2.5px 8px',
              borderRadius: 6,
              color: '#b45309',
              background: 'rgba(245,158,11,0.14)',
              flexShrink: 0,
            }}
            title="Sent to the device — waiting for it to apply the change"
          >
            Pending
          </span>
        )}
        <span style={{ flex: 1 }} />
        <span className="dsCellPencil" style={{ color: theme.textFaint, display: 'flex', flexShrink: 0 }}>
          <EditIcon size={13} />
        </span>
      </span>

      {chips.length > 0 ? (
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 7 }}>
          {chips.map((chip) => (
            <span
              key={chip.key}
              title={chip.label}
              style={{
                display: 'inline-flex',
                alignItems: 'baseline',
                gap: 4,
                fontSize: 11,
                padding: '2.5px 7px',
                borderRadius: 6,
                background: theme.surfaceMuted,
                border: `1px solid ${theme.border}`,
              }}
            >
              <span style={{ color: theme.textFaint, fontWeight: 600 }}>{chip.label}</span>
              <span style={{ color: theme.text, fontWeight: 700 }}>{chip.text}</span>
            </span>
          ))}
        </span>
      ) : (
        <span style={{ display: 'block', fontSize: 11.5, color: theme.textFaint, marginTop: 7 }}>
          Nothing configured — click to set it up
        </span>
      )}

      {stamp && (
        <span style={{ display: 'block', fontSize: 10.5, color: theme.textFaint, marginTop: 6 }}>Updated {stamp}</span>
      )}
    </button>
  );
}

export default SyncCell;
